import { z } from 'zod';

// src/errors.ts
var ERROR_HTTP_STATUS = {
  // ── Sesión y acceso ───────────────────────────────────────────────────
  SESSION_EXPIRED: 401,
  NO_ACTIVE_COMPANY: 400,
  COMPANY_NOT_AUTHORIZED: 403,
  APPLICATION_NOT_LICENSED: 403,
  LICENSE_EXPIRED: 403,
  PERMISSION_DENIED: 403,
  CSRF_INVALID: 403,
  RATE_LIMITED: 429,
  // ── Validación ────────────────────────────────────────────────────────
  VALIDATION_FAILED: 400,
  UNKNOWN_FIELD: 400,
  INVALID_DATE_FORMAT: 400,
  CUSTOM_FIELD_REQUIRED: 422,
  CUSTOM_FIELD_INVALID: 422,
  // ── QR ────────────────────────────────────────────────────────────────
  QR_NOT_FOUND: 404,
  QR_VOIDED: 409,
  QR_ALREADY_LINKED: 409,
  ASSET_ALREADY_LABELLED: 409,
  QR_RANGE_OVERLAP: 409,
  QR_BATCH_TOO_LARGE: 422,
  // ── Activos y movimientos ─────────────────────────────────────────────
  ASSET_NOT_FOUND: 404,
  ASSET_RETIRED: 422,
  ASSET_RENTED: 422,
  ASSET_ASSIGNED: 422,
  ASSET_IN_MAINTENANCE: 422,
  ASSET_NOT_RENTED: 422,
  /// Un recurso de otra empresa nunca revela que existe: 404, jamás 403.
  ASSET_OTHER_COMPANY: 404,
  MOVEMENT_INCOMPATIBLE: 422,
  MOVEMENT_NOT_LAST: 409,
  MOVEMENT_ALREADY_VOIDED: 409,
  DESTINATION_INACTIVE: 422,
  RETROACTIVE_NOT_ALLOWED: 403,
  CONCURRENT_MODIFICATION: 409,
  // ── Alquiler y mantenimiento ──────────────────────────────────────────
  RENTAL_HAS_DELIVERED_LINES: 422,
  RENTAL_ALREADY_RETURNED: 409,
  CUSTOMER_HAS_OPEN_RENTAL: 422,
  MAINTENANCE_ALREADY_OPEN: 409,
  METER_VALUE_DECREASED: 422,
  CHECKLIST_BLOCKING_ITEM: 422,
  // ── Maestros ──────────────────────────────────────────────────────────
  HOLDER_HAS_ASSETS: 409,
  WAREHOUSE_HAS_ASSETS: 409,
  CATEGORY_HAS_ASSETS: 409,
  CODE_ALREADY_EXISTS: 409,
  // ── Archivos ──────────────────────────────────────────────────────────
  FILE_TYPE_NOT_ALLOWED: 422,
  FILE_TOO_LARGE: 413,
  STORAGE_QUOTA_EXCEEDED: 422,
  STORAGE_UNAVAILABLE: 503,
  FILE_TOKEN_INVALID: 403,
  FILE_TOKEN_EXPIRED: 401,
  // ── Importación y reportes ────────────────────────────────────────────
  IMPORT_NOT_VALIDATED: 409,
  IMPORT_MAPPING_INCOMPLETE: 422,
  REPORT_RANGE_TOO_WIDE: 422,
  AUDIT_RANGE_TOO_WIDE: 422,
  EXPORT_TOO_LARGE: 422,
  // ── Transversales ─────────────────────────────────────────────────────
  IDEMPOTENCY_KEY_REUSED: 409,
  PAYLOAD_TOO_LARGE: 413,
  NOT_FOUND: 404,
  SERVICE_UNAVAILABLE: 503,
  INTERNAL_ERROR: 500
};
function httpStatusForError(code) {
  return ERROR_HTTP_STATUS[code];
}
function isErrorCode(valor) {
  return typeof valor === "string" && valor in ERROR_HTTP_STATUS;
}
function isApiErrorBody(valor) {
  if (typeof valor !== "object" || valor === null) return false;
  const posible = valor.error;
  if (typeof posible !== "object" || posible === null) return false;
  const { code, message, requestId } = posible;
  return isErrorCode(code) && typeof message === "string" && typeof requestId === "string";
}

// src/permissions.ts
var PERMISSIONS = [
  // Activos
  "asset.read",
  "asset.create",
  "asset.update",
  "asset.retire",
  /// Permiso aparte: controla la serialización de valores de compra,
  /// depreciación, tarifas de alquiler y costos de mantenimiento.
  "asset.read_financials",
  "asset.link_qr",
  // QR
  "qr.generate",
  "qr.print",
  "qr.void",
  // Movimientos
  "movement.create",
  "movement.void",
  /// Permite fechas retroactivas y ajustes administrativos.
  "movement.adjust",
  // Alquiler
  "rental.create",
  "rental.return",
  "rental.read",
  // Mantenimiento e inspección
  "maintenance.create",
  "maintenance.complete",
  "maintenance.read",
  "inspection.create",
  "inspection.read",
  // Maestros
  "customer.manage",
  "holder.manage",
  "warehouse.manage",
  "zone.manage",
  "category.manage",
  // Datos y reportes
  "import.run",
  "export.run",
  "report.read",
  "audit.read",
  // Configuración
  "company.configure",
  "member.manage",
  "storage.configure",
  "branding.configure"
];
var ROLES = ["admin", "inventory_admin", "operator", "maintenance", "viewer"];
var ROLE_LABELS = {
  admin: "Administrador",
  inventory_admin: "Administrador de inventario",
  operator: "Operador",
  maintenance: "Mantenimiento",
  viewer: "Consulta"
};
var SOLO_LECTURA = [
  "asset.read",
  "rental.read",
  "maintenance.read",
  "inspection.read",
  "report.read"
];
var OPERADOR = [
  ...SOLO_LECTURA,
  "movement.create",
  "inspection.create",
  "rental.return"
];
var MANTENIMIENTO = [
  ...SOLO_LECTURA,
  "movement.create",
  "inspection.create",
  "maintenance.create",
  "maintenance.complete"
];
var ADMIN_INVENTARIO = [
  ...SOLO_LECTURA,
  "asset.create",
  "asset.update",
  "asset.retire",
  "asset.link_qr",
  "qr.generate",
  "qr.print",
  "qr.void",
  "movement.create",
  "movement.void",
  "rental.create",
  "rental.return",
  "maintenance.create",
  "maintenance.complete",
  "inspection.create",
  "customer.manage",
  "holder.manage",
  "warehouse.manage",
  "zone.manage",
  "category.manage",
  "import.run",
  "export.run"
];
var ROLE_PERMISSIONS = {
  admin: PERMISSIONS,
  inventory_admin: ADMIN_INVENTARIO,
  operator: OPERADOR,
  maintenance: MANTENIMIENTO,
  viewer: SOLO_LECTURA
};
function isPermission(valor) {
  return typeof valor === "string" && PERMISSIONS.includes(valor);
}
function isRoleCode(valor) {
  return typeof valor === "string" && ROLES.includes(valor);
}
function permissionsForRole(role) {
  return new Set(ROLE_PERMISSIONS[role]);
}
var FINANCIAL_FIELDS = [
  "purchaseValue",
  "initialValue",
  "residualValue",
  "currentEstimatedValue",
  "accumulatedDepreciation",
  "estimatedTotal",
  "depositAmount",
  "rateAmount",
  "laborCost",
  "partsCost",
  "totalCost"
];
var TAKE_DEFAULT = 25;
var TAKE_MAX = 100;
var TOTAL_MAX_ESTIMATED_ROWS = 5e4;
function encodeCursor(payload) {
  const json = JSON.stringify(payload);
  return btoa(json).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function decodeCursor(cursor) {
  try {
    const base64 = cursor.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64));
    if (typeof payload !== "object" || payload === null) return null;
    const { k, i } = payload;
    if (typeof k !== "string" || typeof i !== "string") return null;
    return { k, i };
  } catch {
    return null;
  }
}
var paginationQuerySchema = z.object({
  take: z.coerce.number().int().min(1).max(TAKE_MAX).default(TAKE_DEFAULT),
  cursor: z.string().min(1).max(500).optional(),
  withTotal: z.enum(["true", "false"]).transform((v) => v === "true").default("false")
});

// src/http.ts
var API_PREFIX = "/api";
var API_VERSION = "v1";
var API_BASE_PATH = `${API_PREFIX}/${API_VERSION}`;
var HEADERS = {
  companyId: "x-company-id",
  csrfToken: "x-csrf-token",
  requestId: "x-request-id",
  idempotencyKey: "idempotency-key",
  idempotentReplay: "x-idempotent-replay",
  rateLimitRemaining: "x-ratelimit-remaining",
  retryAfter: "retry-after"
};
var SESSION_COOKIE = "cqinv_sid";
var ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/csv"
];
function isAllowedMimeType(valor) {
  return ALLOWED_MIME_TYPES.includes(valor);
}

// src/context.ts
function hasPermission(ctx, permission) {
  return ctx.permissions.has(permission);
}
var uuid = z.string().uuid();
var codigo = z.string().trim().min(1).max(30).regex(/^[A-Za-z0-9._-]+$/, "use letras, n\xFAmeros, punto, guion o guion bajo");
var createCategorySchema = z.object({
  name: z.string().trim().min(1).max(150),
  parentId: uuid.nullable().optional(),
  code: z.string().trim().min(1).max(40).nullable().optional(),
  description: z.string().max(4e3).nullable().optional(),
  iconName: z.string().trim().max(60).nullable().optional(),
  color: z.string().trim().max(20).nullable().optional(),
  depreciationPercent: z.coerce.number().min(0.01).max(100).nullable().optional(),
  order: z.number().int().min(0).max(1e4).optional()
});
var patchCategorySchema = createCategorySchema.partial().extend({
  isActive: z.boolean().optional()
});
var createCustomFieldSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,59}$/, "use snake_case, sin may\xFAsculas ni espacios"),
  label: z.string().trim().min(1).max(120),
  type: z.enum(["text", "number", "integer", "decimal", "date", "boolean", "select", "multiselect"]),
  options: z.array(z.string().trim().min(1).max(80)).max(50).nullable().optional(),
  unit: z.string().trim().max(20).nullable().optional(),
  required: z.boolean().optional(),
  order: z.number().int().min(0).max(1e4).optional(),
  isMeter: z.boolean().optional(),
  meterKind: z.enum(["horometer", "odometer", "cycles", "custom"]).nullable().optional(),
  isSearchable: z.boolean().optional(),
  isFilterable: z.boolean().optional(),
  helpText: z.string().max(300).nullable().optional()
});
var patchCustomFieldSchema = createCustomFieldSchema.omit({ key: true, type: true }).partial().extend({
  isActive: z.boolean().optional()
});
var latitud = z.number().min(-90).max(90).nullable();
var longitud = z.number().min(-180).max(180).nullable();
function exigirParDeCoordenadas(valor, ctx) {
  if (valor.latitude === void 0 && valor.longitude === void 0) return;
  const ambosNumeros = typeof valor.latitude === "number" && typeof valor.longitude === "number";
  const ambosNulos = valor.latitude === null && valor.longitude === null;
  if (!ambosNumeros && !ambosNulos) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Indique latitud y longitud juntas, o deje ambas vac\xEDas.",
      path: ["latitude"]
    });
  }
}
var warehouseFields = z.object({
  name: z.string().trim().min(1).max(150),
  code: codigo,
  address: z.string().max(300).nullable().optional(),
  city: z.string().max(100).nullable().optional(),
  description: z.string().max(4e3).nullable().optional(),
  responsibleHolderId: uuid.nullable().optional(),
  latitude: latitud.optional(),
  longitude: longitud.optional()
});
var createWarehouseSchema = warehouseFields.superRefine(exigirParDeCoordenadas);
var patchWarehouseSchema = warehouseFields.partial().extend({
  isActive: z.boolean().optional()
}).superRefine(exigirParDeCoordenadas);
var createWarehouseLocationSchema = z.object({
  parentId: uuid.nullable().optional(),
  kind: z.enum(["zone", "aisle", "shelf", "level", "slot"]),
  name: z.string().trim().min(1).max(120),
  code: z.string().trim().min(1).max(40).nullable().optional()
});
var patchWarehouseLocationSchema = createWarehouseLocationSchema.partial().extend({
  isActive: z.boolean().optional()
});
var workZoneFields = z.object({
  name: z.string().trim().min(1).max(150),
  code: codigo,
  kind: z.enum(["project", "site", "workshop", "plant", "office", "other"]).optional(),
  address: z.string().max(300).nullable().optional(),
  city: z.string().max(100).nullable().optional(),
  description: z.string().max(4e3).nullable().optional(),
  responsibleHolderId: uuid.nullable().optional(),
  startsAt: z.string().datetime({ offset: true }).nullable().optional(),
  endsAt: z.string().datetime({ offset: true }).nullable().optional(),
  latitude: latitud.optional(),
  longitude: longitud.optional()
});
var createWorkZoneSchema = workZoneFields.superRefine(exigirParDeCoordenadas);
var patchWorkZoneSchema = workZoneFields.partial().extend({
  isActive: z.boolean().optional()
}).superRefine(exigirParDeCoordenadas);
var createCustomerSchema = z.object({
  personType: z.enum(["natural", "legal"]),
  legalName: z.string().trim().min(1).max(200),
  tradeName: z.string().trim().max(200).nullable().optional(),
  taxId: z.string().trim().max(40).nullable().optional(),
  contactName: z.string().trim().max(150).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  email: z.string().trim().email().max(200).nullable().optional(),
  address: z.string().max(300).nullable().optional(),
  city: z.string().max(100).nullable().optional(),
  notes: z.string().max(4e3).nullable().optional()
});
var patchCustomerSchema = createCustomerSchema.partial().extend({
  isActive: z.boolean().optional()
});
var createHolderSchema = z.object({
  fullName: z.string().trim().min(1).max(200),
  kind: z.enum(["employee", "contractor", "internal"]).optional(),
  identification: z.string().trim().max(40).nullable().optional(),
  position: z.string().trim().max(120).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  email: z.string().trim().email().max(200).nullable().optional(),
  userId: z.string().trim().max(64).nullable().optional(),
  notes: z.string().max(4e3).nullable().optional(),
  reportsToHolderId: uuid.nullable().optional(),
  supervisedCount: z.number().int().min(0).max(1e5).nullable().optional()
});
var patchHolderSchema = createHolderSchema.partial().extend({
  isActive: z.boolean().optional()
});
var uuid2 = z.string().uuid();
var createQrBatchSchema = z.object({
  quantity: z.number().int().min(1).max(2e4),
  templateId: uuid2.nullable().optional(),
  notes: z.string().max(300).nullable().optional()
});
var linkQrSchema = z.object({
  assetId: uuid2
});
var voidQrSchema = z.object({
  reason: z.string().trim().min(1).max(300).optional()
});
var textoOpcional = (max) => z.union([z.string().max(max), z.literal("")]).nullable();
var upsertPublicQrPolicySchema = z.object({
  enabled: z.boolean(),
  showCompanyName: z.boolean(),
  showAssetName: z.boolean(),
  showVisibleCode: z.boolean(),
  showPhone: z.boolean(),
  phone: textoOpcional(40),
  showEmail: z.boolean(),
  email: z.union([z.string().email().max(200), z.literal("")]).nullable(),
  returnMessage: textoOpcional(500),
  redirectUrl: textoOpcional(400)
});
var uuid3 = z.string().uuid();
var fecha = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
var dinero = z.coerce.string();
var createAssetSchema = z.object({
  name: z.string().trim().min(1).max(200),
  categoryId: uuid3,
  qrCodeId: uuid3.optional(),
  warehouseId: uuid3.nullable().optional(),
  warehouseLocationId: uuid3.nullable().optional(),
  internalCode: z.string().trim().min(1).max(64).nullable().optional(),
  description: z.string().max(8e3).nullable().optional(),
  brand: z.string().trim().max(100).nullable().optional(),
  model: z.string().trim().max(100).nullable().optional(),
  serialNumber: z.string().trim().max(120).nullable().optional(),
  manufacturer: z.string().trim().max(120).nullable().optional(),
  notes: z.string().max(8e3).nullable().optional(),
  groupName: z.string().trim().max(120).nullable().optional(),
  customValues: z.record(z.unknown()).optional(),
  operationalStatusCode: z.string().trim().min(1).max(40).optional(),
  purchaseDate: fecha.nullable().optional(),
  entryDate: fecha.nullable().optional(),
  supplierName: z.string().trim().max(200).nullable().optional(),
  invoiceNumber: z.string().trim().max(60).nullable().optional(),
  purchaseValue: dinero.nullable().optional(),
  currency: z.string().trim().length(3).nullable().optional(),
  warrantyMonths: z.number().int().min(0).max(600).nullable().optional(),
  warrantyEndsAt: fecha.nullable().optional(),
  initialValue: dinero.nullable().optional(),
  usefulLifeMonths: z.number().int().min(0).max(600).nullable().optional(),
  residualValue: dinero.nullable().optional(),
  depreciationStartDate: fecha.nullable().optional(),
  depreciationMethod: z.enum(["none", "linear"]).optional()
});
var patchAssetSchema = createAssetSchema.omit({ categoryId: true, qrCodeId: true, warehouseId: true, warehouseLocationId: true }).partial().extend({
  categoryId: uuid3.optional()
});
var retireAssetSchema = z.object({
  reason: z.string().trim().min(1).max(300)
});
var listAssetsQuerySchema = paginationQuerySchema.extend({
  q: z.string().max(200).optional(),
  withoutQr: z.enum(["true", "false"]).transform((v) => v === "true").optional(),
  categoryId: uuid3.optional(),
  operationalStatusCode: z.string().max(40).optional(),
  logisticStatusCode: z.string().max(40).optional(),
  warehouseId: uuid3.optional(),
  workZoneId: uuid3.optional(),
  holderId: uuid3.optional(),
  customerId: uuid3.optional(),
  includeRetired: z.enum(["true", "false"]).transform((v) => v === "true").optional()
});
var uuid4 = z.string().uuid();
var locationKind = z.enum(["warehouse", "zone", "holder", "customer", "maintenance", "none"]);
var movementDestinationSchema = z.object({
  kind: locationKind,
  warehouseId: uuid4.nullable().optional(),
  warehouseLocationId: uuid4.nullable().optional(),
  workZoneId: uuid4.nullable().optional(),
  holderId: uuid4.nullable().optional(),
  customerId: uuid4.nullable().optional(),
  maintenanceOrderId: uuid4.nullable().optional(),
  isLoan: z.boolean().optional()
});
var createMovementSchema = z.object({
  typeCode: z.string().min(1).max(40).optional(),
  destination: movementDestinationSchema,
  occurredAt: z.string().datetime().optional(),
  reason: z.string().trim().max(500).nullable().optional(),
  notes: z.string().max(8e3).nullable().optional(),
  lastMovementId: uuid4.nullable().optional(),
  warningsConfirmed: z.boolean().optional(),
  channel: z.enum(["scan", "manual", "bulk", "import", "api", "job"]).optional(),
  toOperationalStatusCode: z.string().min(1).max(40).optional()
});
var voidMovementSchema = z.object({
  reason: z.string().trim().min(1).max(500)
});
var createScanSessionSchema = z.object({
  typeCode: z.string().min(1).max(40).optional(),
  destination: movementDestinationSchema,
  reason: z.string().trim().max(500).nullable().optional()
});
var addScanSessionItemSchema = z.object({
  token: z.string().trim().min(1).max(200)
});
var adjustMovementSchema = createMovementSchema.extend({
  assetId: uuid4,
  reason: z.string().trim().min(1).max(500)
});
var adjustLocationBatchSchema = z.object({
  assetIds: z.array(uuid4).min(1).max(100),
  warehouseId: uuid4,
  warehouseLocationId: uuid4.nullable().optional(),
  reason: z.string().trim().min(1).max(500)
});
var listMovementsQuerySchema = z.object({
  take: z.coerce.number().int().min(1).max(100).optional(),
  cursor: z.string().min(1).optional(),
  assetId: uuid4.optional()
});
var listAuditQuerySchema = z.object({
  from: z.string().datetime(),
  to: z.string().datetime(),
  take: z.coerce.number().int().min(1).max(100).optional(),
  action: z.string().max(80).optional(),
  entityType: z.string().max(40).optional()
});
var entityType = z.enum([
  "asset",
  "movement",
  "inspection",
  "maintenance_order",
  "rental",
  "company",
  "import_job",
  "export_job"
]);
var listFilesQuerySchema = z.object({
  entityType,
  entityId: z.string().uuid()
});
var createFileQuerySchema = z.object({
  entityType,
  entityId: z.string().uuid(),
  kind: z.string().min(1).max(40).default("asset_photo"),
  originalName: z.string().min(1).max(255).optional()
});
var createStorageConfigSchema = z.object({
  kind: z.enum(["local", "s3", "gdrive", "sftp", "ftp", "webdav"]),
  displayName: z.string().trim().min(1).max(120),
  settings: z.record(z.unknown()).default({}),
  secrets: z.record(z.string()).optional(),
  quotaBytes: z.coerce.number().int().positive().nullable().optional(),
  quotaObjects: z.coerce.number().int().positive().nullable().optional(),
  isDefault: z.boolean().optional()
});
var uuid5 = z.string().uuid();
var datetime = z.string().datetime();
var createRentalSchema = z.object({
  customerId: uuid5,
  startsAt: datetime.optional(),
  expectedEndAt: datetime,
  isLoan: z.boolean().optional(),
  notes: z.string().max(8e3).nullable().optional(),
  depositAmount: z.number().nonnegative().nullable().optional(),
  currency: z.string().length(3).nullable().optional(),
  assetIds: z.array(uuid5).max(200).optional(),
  deliverNow: z.boolean().optional()
});
var patchRentalSchema = z.object({
  expectedEndAt: datetime.optional(),
  notes: z.string().max(8e3).nullable().optional(),
  depositAmount: z.number().nonnegative().nullable().optional(),
  currency: z.string().length(3).nullable().optional()
});
var addRentalLinesSchema = z.object({
  assetIds: z.array(uuid5).min(1).max(200)
});
var deliverRentalSchema = z.object({
  assetIds: z.array(uuid5).min(1).max(200).optional()
});
var cancelRentalSchema = z.object({
  reason: z.string().trim().min(1).max(500)
});
var rentalReturnLineSchema = z.object({
  assetId: uuid5,
  condition: z.enum(["ok", "damaged", "missing", "needs_maintenance"]),
  toOperationalStatusCode: z.string().min(1).max(40).optional(),
  notes: z.string().max(4e3).nullable().optional(),
  openMaintenance: z.boolean().optional()
});
var createRentalReturnSchema = z.object({
  warehouseId: uuid5,
  warehouseLocationId: uuid5.nullable().optional(),
  lines: z.array(rentalReturnLineSchema).min(1).max(200)
});
var listRentalsQuerySchema = z.object({
  take: z.coerce.number().int().min(1).max(100).optional(),
  status: z.enum(["draft", "delivered", "partially_returned", "returned", "cancelled"]).optional()
});
var uuid6 = z.string().uuid();
var createMaintenanceOrderSchema = z.object({
  assetId: uuid6,
  kind: z.enum(["preventive", "corrective", "predictive", "legal"]).default("corrective"),
  problemDescription: z.string().trim().min(1).max(8e3),
  toOperationalStatusCode: z.string().min(1).max(40).optional(),
  sendNow: z.boolean().optional(),
  notes: z.string().max(8e3).nullable().optional()
});
var patchMaintenanceOrderSchema = z.object({
  problemDescription: z.string().trim().min(1).max(8e3).optional(),
  workPerformed: z.string().max(8e3).nullable().optional(),
  notes: z.string().max(8e3).nullable().optional(),
  laborCost: z.number().nonnegative().nullable().optional(),
  partsCost: z.number().nonnegative().nullable().optional(),
  currency: z.string().length(3).nullable().optional()
});
var completeMaintenanceSchema = z.object({
  result: z.enum(["repaired", "partially_repaired", "not_repairable", "no_fault_found"]),
  destination: movementDestinationSchema,
  toOperationalStatusCode: z.string().min(1).max(40).optional(),
  workPerformed: z.string().max(8e3).nullable().optional(),
  retire: z.boolean().optional()
});
var cancelMaintenanceSchema = z.object({
  reason: z.string().trim().min(1).max(500)
});
var createInspectionSchema = z.object({
  assetId: uuid6,
  condition: z.enum(["good", "fair", "poor", "out_of_service"]),
  notes: z.string().max(8e3).nullable().optional(),
  checklistId: uuid6.nullable().optional(),
  answers: z.record(z.unknown()).optional(),
  openMaintenance: z.boolean().optional()
});
var createMeterSchema = z.object({
  kind: z.enum(["horometer", "odometer", "cycles", "custom"]),
  label: z.string().trim().min(1).max(80),
  unit: z.string().trim().min(1).max(20)
});
var createMeterReadingSchema = z.object({
  value: z.number(),
  notes: z.string().max(300).nullable().optional(),
  resetFromValue: z.number().nullable().optional()
});
var createChecklistSchema = z.object({
  name: z.string().trim().min(1).max(150),
  purpose: z.enum(["inspection", "maintenance", "delivery", "return"]),
  categoryId: uuid6.nullable().optional(),
  items: z.array(
    z.object({
      key: z.string().trim().min(1).max(60),
      label: z.string().trim().min(1).max(200),
      type: z.enum(["boolean", "scale", "text", "number", "photo"]).default("boolean"),
      required: z.boolean().optional(),
      isBlocking: z.boolean().optional(),
      order: z.number().int().min(0).max(1e4).optional()
    })
  ).max(80).optional()
});
var createImportSchema = z.object({
  target: z.enum(["assets", "locations", "customers", "holders", "categories"]).default("assets"),
  content: z.string().min(1).max(5e6),
  filename: z.string().max(255).optional(),
  createMissingMasters: z.boolean().optional()
});
var importMappingSchema = z.object({
  columnMap: z.record(z.string().min(1).max(80))
});
var listReportsQuerySchema = z.object({
  take: z.coerce.number().int().min(1).max(100).optional(),
  cursor: z.string().optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  categoryId: z.string().uuid().optional(),
  warehouseId: z.string().uuid().optional(),
  workZoneId: z.string().uuid().optional(),
  holderId: z.string().uuid().optional(),
  customerId: z.string().uuid().optional(),
  logisticStatusCode: z.string().max(40).optional(),
  operationalStatusCode: z.string().max(40).optional(),
  includeRetired: z.enum(["true", "false"]).optional(),
  format: z.enum(["csv", "xlsx", "pdf"]).optional()
});
var asOfQuerySchema = z.object({
  date: z.string().datetime(),
  take: z.coerce.number().int().min(1).max(200).optional()
});
var createExportSchema = z.object({
  reportCode: z.string().min(1).max(80),
  format: z.enum(["csv", "xlsx", "pdf"]).default("csv"),
  filters: z.record(z.string()).optional(),
  columns: z.array(z.string().trim().min(1).max(64)).max(60).optional()
});
var hsl = z.string().max(40);
var brandColorsSchema = z.object({
  primary: hsl.optional(),
  primaryFg: hsl.optional(),
  secondary: hsl.optional(),
  secondaryFg: hsl.optional(),
  surface: hsl.optional(),
  surface2: hsl.optional(),
  text: hsl.optional(),
  textMuted: hsl.optional(),
  border: hsl.optional(),
  success: hsl.optional(),
  warning: hsl.optional(),
  danger: hsl.optional(),
  radius: z.string().max(20).optional()
});
var upsertBrandThemeSchema = z.object({
  appName: z.string().min(1).max(80).optional(),
  commercialName: z.string().min(1).max(120).optional(),
  colors: brandColorsSchema.optional(),
  loginTitle: z.string().max(160).nullable().optional(),
  loginSubtitle: z.string().max(300).nullable().optional(),
  loginFooter: z.string().max(500).nullable().optional(),
  contact: z.record(z.unknown()).optional(),
  legal: z.record(z.unknown()).optional(),
  documentHeader: z.record(z.unknown()).optional(),
  showPoweredBy: z.boolean().optional()
});
var themeQuerySchema = z.object({
  org: z.string().max(80).optional(),
  companyId: z.string().uuid().optional()
});

export { ALLOWED_MIME_TYPES, API_BASE_PATH, API_PREFIX, API_VERSION, ERROR_HTTP_STATUS, FINANCIAL_FIELDS, HEADERS, PERMISSIONS, ROLES, ROLE_LABELS, ROLE_PERMISSIONS, SESSION_COOKIE, TAKE_DEFAULT, TAKE_MAX, TOTAL_MAX_ESTIMATED_ROWS, addRentalLinesSchema, addScanSessionItemSchema, adjustLocationBatchSchema, adjustMovementSchema, asOfQuerySchema, brandColorsSchema, cancelMaintenanceSchema, cancelRentalSchema, completeMaintenanceSchema, createAssetSchema, createCategorySchema, createChecklistSchema, createCustomFieldSchema, createCustomerSchema, createExportSchema, createFileQuerySchema, createHolderSchema, createImportSchema, createInspectionSchema, createMaintenanceOrderSchema, createMeterReadingSchema, createMeterSchema, createMovementSchema, createQrBatchSchema, createRentalReturnSchema, createRentalSchema, createScanSessionSchema, createStorageConfigSchema, createWarehouseLocationSchema, createWarehouseSchema, createWorkZoneSchema, decodeCursor, deliverRentalSchema, encodeCursor, hasPermission, httpStatusForError, importMappingSchema, isAllowedMimeType, isApiErrorBody, isErrorCode, isPermission, isRoleCode, linkQrSchema, listAssetsQuerySchema, listAuditQuerySchema, listFilesQuerySchema, listMovementsQuerySchema, listRentalsQuerySchema, listReportsQuerySchema, movementDestinationSchema, paginationQuerySchema, patchAssetSchema, patchCategorySchema, patchCustomFieldSchema, patchCustomerSchema, patchHolderSchema, patchMaintenanceOrderSchema, patchRentalSchema, patchWarehouseLocationSchema, patchWarehouseSchema, patchWorkZoneSchema, permissionsForRole, rentalReturnLineSchema, retireAssetSchema, themeQuerySchema, upsertBrandThemeSchema, upsertPublicQrPolicySchema, voidMovementSchema, voidQrSchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map